/* eslint-disable react/no-unescaped-entities */
/**
 * /hustleup/organisers — landing page for existing Meetup (or similar)
 * organisers. Generates a tracked invite link they can share with their
 * existing members without breaching their current platform's TOS.
 *
 * Hero: "Quietly move your community to HustleUp. Free forever."
 * CTA  : create invite → share unique URL → members land on /hustleup/join?ref=XXX
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Users, ArrowRight, ShieldCheck, CheckCircle2, Copy, Mail,
  Loader2, Sparkles, Heart, Lock, Globe2, TrendingUp,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

export default function HustleUpOrganisersPage() {
  const [step, setStep]           = useState("intro");     // intro | form | done
  const [name, setName]           = useState("");
  const [email, setEmail]         = useState("");
  const [platform, setPlatform]   = useState("meetup");
  const [groupName, setGroupName] = useState("");
  const [memCount, setMemCount]   = useState("");
  const [note, setNote]           = useState("");
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState("");
  const [invite, setInvite]       = useState(null);
  const [copied, setCopied]       = useState(false);
  const [stats, setStats]         = useState(null);

  useEffect(() => {
    api.get("/api/hustleup/invites/stats/live")
      .then(r => setStats(r.data))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post(`/api/hustleup/invites`, {
        organiser_name:     name,
        organiser_email:    email,
        source_platform:    platform,
        source_group_name:  groupName,
        member_count_hint:  memCount ? parseInt(memCount, 10) : null,
        note:               note || null,
      });
      setInvite(r.data); setStep("done");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  const inviteUrl = invite
    ? `${window.location.origin}/hustleup/join?ref=${invite.token}`
    : "";

  const copy = async () => {
    try { await navigator.clipboard.writeText(inviteUrl); setCopied(true);
          setTimeout(() => setCopied(false), 1800); }
    catch { /* ignore */ }
  };

  return (
    <div style={s.root} data-testid="hu-organisers-root">
      <header style={s.header}>
        <a href="/hustleup" style={s.brand}>
          <Users size={20} />
          <span>Hustle<span style={{ color: "#fbbf24" }}>Up</span></span>
        </a>
        <a href="/hustleup" style={s.headerLink}>Back to HustleUp</a>
      </header>

      <main style={s.main}>
        {step === "intro" && (
          <section data-testid="hu-org-intro">
            <span style={s.eyebrow}>
              <Sparkles size={11} /> Built for Meetup organisers
            </span>
            {stats && (stats.signups_24h > 0 || stats.signups_lifetime > 0) && (
              <div style={s.liveStrip} data-testid="hu-org-live-stats">
                <span style={s.livePulse} />
                <TrendingUp size={13} color="#16a34a" />
                <span>
                  <strong>{stats.signups_24h}</strong>{" "}
                  {stats.signups_24h === 1 ? "person joined" : "people joined"}{" "}
                  in the last 24h ·{" "}
                  <strong>{stats.signups_lifetime}</strong> members all-time ·{" "}
                  <strong>{stats.organisers_total}</strong> organisers switched
                </span>
              </div>
            )}
            <h1 style={s.h1}>
              Quietly move your community to <span style={s.accent}>HustleUp.</span>
            </h1>
            <p style={s.lead}>
              Meetup's subscription fees are pushing organisers out the door —
              and members feel orphaned. HustleUp.au is free forever
              for organisers <em>and</em> members. No copies, no scraping —
              your members opt in themselves via a unique invite link you
              share, so nothing breaches your existing Meetup terms.
            </p>

            <div style={s.cardRow}>
              {[
                { icon: Heart,  t: "Free forever",
                  d: "$0 for organisers. $0 for members. No premium tier, no upsells." },
                { icon: ShieldCheck, t: "Verified humans only",
                  d: "Every member completes email + SMS + AU postcode verification before joining." },
                { icon: Lock,    t: "Your data, your way",
                  d: "We never scrape Meetup. Your members re-join voluntarily via your link." },
                { icon: Globe2,  t: "Australia-first",
                  d: "Built for AU communities — from Darwin barra fishing to Hobart code clubs." },
              ].map(({ icon: Icon, t, d }) => (
                <div key={t} style={s.card}>
                  <Icon size={20} color="#0f172a" />
                  <h3 style={s.cardT}>{t}</h3>
                  <p style={s.cardD}>{d}</p>
                </div>
              ))}
            </div>

            <h2 style={s.h2}>How the switch works</h2>
            <ol style={s.steps}>
              <li><strong>You generate a free invite link below.</strong> Takes 30 seconds.</li>
              <li><strong>Post the link</strong> in your Meetup group chat, event
                  description, or send via your usual newsletter — wherever
                  you're already allowed to communicate with members.</li>
              <li><strong>Members opt in themselves</strong> on /hustleup/join
                  — 4-step verified signup, no data transfer needed.</li>
              <li><strong>You spin up your free HustleUp group</strong> — same
                  topics, same vibe, zero monthly fee.</li>
              <li><strong>We attribute every signup to your link</strong> so
                  you can see your community rebuild in real time.</li>
            </ol>

            <button
              onClick={() => setStep("form")}
              style={s.cta}
              data-testid="hu-org-start-btn"
            >
              Get my free invite link <ArrowRight size={16} />
            </button>

            <p style={s.fine}>
              <ShieldCheck size={11} /> We don't ask for your Meetup
              login or member list — ever. You stay in control.
            </p>
          </section>
        )}

        {step === "form" && (
          <section data-testid="hu-org-form">
            <h1 style={s.h1}>Tell us about your community</h1>
            <p style={s.lead}>
              Just the basics so we can credit signups back to you. No
              member data — just yours.
            </p>

            <form onSubmit={submit} style={{ marginTop: 24 }}>
              <label style={s.lbl}>Your name</label>
              <input style={s.input} required value={name}
                     onChange={e => setName(e.target.value)}
                     placeholder="e.g. Sam Brisbane"
                     data-testid="hu-org-name" />

              <label style={s.lbl}>Your email (we'll send the dashboard link)</label>
              <input style={s.input} required type="email" value={email}
                     onChange={e => setEmail(e.target.value)}
                     placeholder="you@example.com"
                     data-testid="hu-org-email" />

              <label style={s.lbl}>Where's your community right now?</label>
              <select style={s.input} value={platform}
                      onChange={e => setPlatform(e.target.value)}
                      data-testid="hu-org-platform">
                <option value="meetup">Meetup.com</option>
                <option value="facebook">Facebook Group</option>
                <option value="eventbrite">Eventbrite</option>
                <option value="other">Somewhere else</option>
              </select>

              <label style={s.lbl}>Your existing group name</label>
              <input style={s.input} required value={groupName}
                     onChange={e => setGroupName(e.target.value)}
                     placeholder="e.g. Brisbane Sunset Hikers"
                     data-testid="hu-org-groupname" />

              <label style={s.lbl}>Rough member count (optional)</label>
              <input style={s.input} type="number" min="0" value={memCount}
                     onChange={e => setMemCount(e.target.value)}
                     placeholder="e.g. 850"
                     data-testid="hu-org-count" />

              <label style={s.lbl}>Anything else (optional)</label>
              <textarea style={{ ...s.input, minHeight: 80 }} value={note}
                        onChange={e => setNote(e.target.value)}
                        placeholder="Why you're switching, any questions for us…"
                        data-testid="hu-org-note" />

              {err && <div style={s.err} data-testid="hu-org-err">{err}</div>}

              <button type="submit" disabled={busy} style={s.cta}
                      data-testid="hu-org-submit-btn">
                {busy ? <><Loader2 size={16} className="spin" /> Generating…</>
                      : <>Generate my invite link <ArrowRight size={16} /></>}
              </button>
            </form>
          </section>
        )}

        {step === "done" && invite && (
          <section data-testid="hu-org-done">
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8,
                          background: "#dcfce7", color: "#166534", padding: "6px 14px",
                          borderRadius: 999, fontSize: 12, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 18 }}>
              <CheckCircle2 size={13} /> Link ready
            </div>
            <h1 style={s.h1}>Here's your invite link</h1>
            <p style={s.lead}>
              Share this in your Meetup group chat, event descriptions, or
              members-only emails. Every signup gets attributed back to you.
            </p>

            <div style={s.linkBox} data-testid="hu-org-link-box">
              <code style={s.linkCode}>{inviteUrl}</code>
              <button onClick={copy} style={s.copyBtn} data-testid="hu-org-copy-btn">
                <Copy size={14} /> {copied ? "Copied!" : "Copy"}
              </button>
            </div>

            <div style={s.statRow}>
              <div style={s.stat}>
                <div style={s.statN}>0</div>
                <div style={s.statL}>Clicks</div>
              </div>
              <div style={s.stat}>
                <div style={s.statN}>0</div>
                <div style={s.statL}>Signups</div>
              </div>
              <div style={s.stat}>
                <div style={s.statN}>Free</div>
                <div style={s.statL}>Forever</div>
              </div>
            </div>

            <h2 style={s.h2}>Suggested message</h2>
            <div style={s.snippetBox} data-testid="hu-org-snippet">
              <p style={{ margin: 0 }}>
                Hey everyone — just a heads-up that I've started parking
                our group on <strong>HustleUp.au</strong>, a brand-new
                Australian community platform that's 100% free for both
                organisers and members. Same vibe, zero fees. If you'd
                like to keep getting our event invites without anything
                breaking on Meetup, sign up here in 60 seconds:{" "}
                <a href={inviteUrl}>{inviteUrl}</a>
              </p>
            </div>

            <a href={`mailto:?subject=Joining%20HustleUp.au&body=${encodeURIComponent(
              `Hey — I'm parking our group on HustleUp.au (free for everyone). Sign up here: ${inviteUrl}`
            )}`} style={s.cta} data-testid="hu-org-email-btn">
              <Mail size={16} /> Send via email
            </a>

            <p style={{ ...s.fine, marginTop: 24 }}>
              We emailed a copy of this link to <strong>{invite.organiser_email}</strong>{" "}
              along with a link to your live signup dashboard.
            </p>
          </section>
        )}
      </main>

      <footer style={s.footer}>
        <small>HustleUp.au · Free forever · Built in Australia</small>
      </footer>

      <style>{`.spin{animation:spin 1s linear infinite}
              @keyframes spin{to{transform:rotate(360deg)}}
              @keyframes huPulse{0%,100%{opacity:.4;transform:scale(.85)}50%{opacity:1;transform:scale(1.15)}}`}</style>
    </div>
  );
}

const s = {
  root: { background: "#fffef5", minHeight: "100vh",
          fontFamily: "Inter, system-ui, sans-serif", color: "#0f172a" },
  header: { display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "18px 32px",
            background: "#0f172a", color: "white", position: "sticky",
            top: 0, zIndex: 10 },
  brand: { display: "inline-flex", alignItems: "center", gap: 8,
           color: "#fbbf24", textDecoration: "none", fontWeight: 800,
           fontFamily: "Georgia, serif", fontSize: 20 },
  headerLink: { color: "#94a3b8", fontSize: 13, textDecoration: "none" },
  main: { maxWidth: 720, margin: "0 auto", padding: "44px 22px 80px" },
  eyebrow: { display: "inline-flex", alignItems: "center", gap: 6,
             background: "#fef3c7", color: "#92400e",
             padding: "5px 14px", borderRadius: 999,
             fontSize: 11, fontWeight: 800, textTransform: "uppercase",
             letterSpacing: 0.6, marginBottom: 16 },
  h1: { fontFamily: "Georgia, serif", fontSize: 38, fontWeight: 800,
        letterSpacing: "-0.03em", lineHeight: 1.1, margin: "0 0 18px" },
  h2: { fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 800,
        margin: "40px 0 14px", letterSpacing: "-0.02em" },
  accent: { color: "#fbbf24" },
  lead: { fontSize: 17, color: "#475569", lineHeight: 1.6, margin: "0 0 24px" },
  cardRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
             gap: 12, margin: "28px 0" },
  card: { background: "white", border: "1px solid #e8e2d4", borderRadius: 14,
          padding: 18 },
  cardT: { fontSize: 14, fontWeight: 800, margin: "10px 0 5px" },
  cardD: { fontSize: 13, color: "#64748b", lineHeight: 1.5, margin: 0 },
  steps: { paddingLeft: 22, color: "#1f2937", fontSize: 15, lineHeight: 1.7 },
  cta: { display: "inline-flex", alignItems: "center", gap: 8,
         background: "#0f172a", color: "#fbbf24", border: "none",
         padding: "14px 26px", borderRadius: 999, fontWeight: 800,
         fontSize: 15, cursor: "pointer", marginTop: 18,
         textDecoration: "none" },
  fine: { fontSize: 12, color: "#94a3b8", marginTop: 18,
          display: "inline-flex", alignItems: "center", gap: 6 },
  lbl: { display: "block", fontSize: 11, fontWeight: 800, color: "#64748b",
         textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6,
         marginTop: 14 },
  input: { width: "100%", padding: "12px 14px", border: "1px solid #e8e2d4",
           borderRadius: 10, fontSize: 15, fontFamily: "inherit",
           background: "white", outline: "none", boxSizing: "border-box" },
  err: { background: "#fee2e2", color: "#991b1b", padding: 10,
         borderRadius: 8, fontSize: 13, marginTop: 16 },
  linkBox: { display: "flex", alignItems: "center", gap: 12,
             background: "white", border: "1px solid #e8e2d4",
             borderRadius: 12, padding: 14, marginBottom: 20 },
  linkCode: { flex: 1, fontFamily: "ui-monospace, monospace",
              fontSize: 13, color: "#0f172a", wordBreak: "break-all" },
  copyBtn: { display: "inline-flex", alignItems: "center", gap: 6,
             background: "#fef3c7", color: "#92400e", border: "none",
             padding: "8px 14px", borderRadius: 999, fontWeight: 700,
             fontSize: 12, cursor: "pointer" },
  statRow: { display: "grid", gridTemplateColumns: "repeat(3,1fr)",
             gap: 12, marginBottom: 26 },
  stat: { background: "white", border: "1px solid #e8e2d4",
          borderRadius: 12, padding: 14, textAlign: "center" },
  statN: { fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 800 },
  statL: { fontSize: 11, color: "#64748b", textTransform: "uppercase",
           letterSpacing: 0.6, marginTop: 2 },
  snippetBox: { background: "#fffbeb", border: "1px solid #fde68a",
                borderRadius: 12, padding: 18, color: "#1f2937",
                fontSize: 14, lineHeight: 1.6 },
  footer: { textAlign: "center", padding: "32px 20px", color: "#94a3b8",
            fontSize: 12 },
  liveStrip: { display: "inline-flex", alignItems: "center", gap: 8,
                background: "#dcfce7", color: "#166534",
                padding: "7px 14px", borderRadius: 999, fontSize: 12,
                fontWeight: 600, marginBottom: 14,
                border: "1px solid #bbf7d0" },
  livePulse: { width: 7, height: 7, borderRadius: "50%",
                background: "#16a34a",
                animation: "huPulse 1.4s ease-in-out infinite" },
};
